import { getConfigManager } from '../../config';
import { MULTICALL_CONTRACT_ADDRESS } from '../../constants';
import { Decimal, DEFAULT_DECIMAL_PRECISION } from '../../datastructures';
import { InterestRateStateData, NostraToken, Nullable } from '../../interfaces';
import { MultiCallContract } from '../../services';
import nostraTokenListAtom from './nostraTokenListAtom';
import { createAsyncAtom } from '../../lib/state';
import { RpcProvider } from 'starknet';
import rpcProviderAtom from '../rpcProviderAtom';

export type InterestStateMap = { [debtTokenAddress: string]: InterestRateStateData };

const POLLING_INTERVAL_IN_MS = 300000; // 5 minutes
const RESPONSE_CHUNK_SIZE = 10;

const fetchData = async (debtTokens: NostraToken[], provider: RpcProvider) => {
  try {
    const config = getConfigManager().getConfig();

    const calldata = debtTokens.map(debtToken => {
      return {
        contractAddress: config.interestRateModel,
        entrypoint: 'getInterestState',
        calldata: [debtToken.address],
      };
    });

    const multiCallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

    const result = await multiCallContract.aggregate(calldata);

    if (!result) {
      return null;
    }

    const interestStateMap: InterestStateMap = {};

    for (let i = 0; i < result.length; i += RESPONSE_CHUNK_SIZE) {
      // Index 1,2 is lendingRate
      // Index 3,4 is borrowRate
      const lendingRate = new Decimal({ low: result[i + 1], high: result[i + 2] }, DEFAULT_DECIMAL_PRECISION);
      const borrowRate = new Decimal({ low: result[i + 3], high: result[i + 4] }, DEFAULT_DECIMAL_PRECISION);

      // (1+ contract_rate/365)^365 -1
      const lendingApy = Decimal.parse(Math.pow(Math.E, Number(lendingRate.toString())) - 1, 0);

      interestStateMap[debtTokens[i / RESPONSE_CHUNK_SIZE].address] = {
        lendingRate: lendingApy,
        borrowRate,
      };
    }

    return interestStateMap;
  } catch (error) {
    console.error(`interestStateAtom - Failed to load interest state!`, error);
    return null;
  }
};

const interestStateAtom = createAsyncAtom<Nullable<InterestStateMap>>(
  async get => {
    const nostraTokenList = await get(nostraTokenListAtom);
    const provider = await get(rpcProviderAtom);

    const debtTokens = nostraTokenList.filter(nostraToken => nostraToken.variant === 'debt');

    const result = await fetchData(debtTokens, provider);

    return result;
  },
  POLLING_INTERVAL_IN_MS,
  ['user-refresh'],
);

export default interestStateAtom;
